import { ChartOptions } from 'chart.js'
import { HttpResponse } from '@angular/common/http'
import { saveAs } from 'file-saver'
import { Tooltip } from 'bootstrap'

export function keepOnlyDigits(value: string): string {
    if (!value) {
        return ''
    }
    return value.replace(/\D/g, '')
}

export function validateOnlyDigitsAndSize(event: KeyboardEvent, value: string, size: number): void {
    const allowedKeys = ['Backspace', 'Delete', 'Enter', 'ArrowLeft', 'ArrowRight']

    if (!(/\d/.test(event.key) || allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey)) {
        event.preventDefault()
    }

    if (keepOnlyDigits(value).length >= size && !allowedKeys.includes(event.key) && !event.ctrlKey && !event.metaKey) {
        event.preventDefault()
    }
}

export function downloadPdfFile(httpResponse: HttpResponse<Blob>): void {
    const filenameWrap = httpResponse.headers.get('content-disposition').split('filename=')[1]
    const filename = filenameWrap.substring(1, filenameWrap.length - 1)
    saveAs(httpResponse.body, filename)
}

export function getGenericChartOptions(): ChartOptions {
    return {
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                titleFont: {
                    size: 0,
                },
            },
        },
        responsive: true,
        scales: {
            x: {
                ticks: {
                    padding: 10,
                    font: {
                        weight: 'bold',
                        size: 13,
                    },
                },
                border: {
                    display: true,
                    width: 2,
                    z: 999,
                    color: '#e31b18',
                },
                grid: {
                    display: false,
                    drawOnChartArea: false,
                    drawTicks: false,
                },
                stacked: true,
            },
            y: {
                ticks: {
                    display: false,
                },
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                    drawOnChartArea: false,
                    drawTicks: false,
                },
                stacked: true,
            },
        },
    }
}

export function activateTooltipsForComponent(): void {
    const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
    ;[...tooltipTriggerList].map((tooltipTriggerEl) => new Tooltip(tooltipTriggerEl))
}

export function downloadFile(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = fileName
    a.click()
    URL.revokeObjectURL(objectUrl)
}

export function deepPatch<T extends object, S extends object>(target: T, source: S): T & S {
    // 1. Create a shallow copy of the target to start building the new object
    const newObject = { ...target } as T & S

    // 2. Iterate through the source object's keys
    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const targetValue = (target as unknown)[key] // Get value from original target
            const sourceValue = source[key] // Get value from source

            // Check if both are objects (and not null or arrays)
            if (
                typeof targetValue === 'object' &&
                targetValue !== null &&
                !Array.isArray(targetValue) &&
                typeof sourceValue === 'object' &&
                sourceValue !== null &&
                !Array.isArray(sourceValue)
            ) {
                // Recursively merge nested objects
                /* eslint-disable no-extra-semi */
                ;(newObject as unknown)[key] = deepPatch(targetValue, sourceValue)
            } else if (Array.isArray(sourceValue)) {
                // If the source value is an array, replace the target's array with a shallow copy of the source array.
                // For truly deep array merging (e.g., by element ID), this part would need significant custom logic.
                ;(newObject as unknown)[key] = [...sourceValue]
            } else {
                // For primitive values, null, or if types differ (e.g., object becomes primitive),
                // or if source is array and target isn't (or vice versa), just overwrite.
                ;(newObject as unknown)[key] = sourceValue
                /* eslint-enable */
            }
        }
    }

    return newObject
}

export const focusOnElement = (element: HTMLElement): void => {
    if (element) {
        element.focus()
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        })
    }
}

export const toCarteBtpFormat = (carteBtp: string): string => {
    if (!carteBtp) {
        return ''
    }
    const chunks = carteBtp.replace(/\s/g, '').match(/.{1,3}/g)
    if (chunks) {
        return chunks.join(' ')
    }
    return carteBtp
}

export function decodeBase64(value: string): string {
    if (!value) return ''
    try {
        const binString = atob(value)
        const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0)!)
        return new TextDecoder().decode(bytes)
    } catch {
        return value
    }
}
